import { SearchOutlined } from "@mui/icons-material";
import {
  Alert, Box, Button, Chip, Divider, InputAdornment, Paper, Stack,
  TextField, Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, loadFlowDiagnostic } from "./api";
import { operationalStatusLabel } from "./operationalStatus";
import { PageHeader } from "./PageHeader";

export function FlowDiagnosticPage({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState("");
  const [reference, setReference] = useState("");
  const diagnostic = useQuery({
    queryKey: ["flow-diagnostic", reference],
    queryFn: () => loadFlowDiagnostic(reference), enabled: reference.length >= 3,
    retry: false
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value.length >= 3) setReference(value);
  };
  const errorMessage = diagnostic.error instanceof ApiError
    && (diagnostic.error.status ?? 0) >= 500
    ? t("transverse.diagnostic.calculationError")
    : diagnostic.error instanceof Error
      ? diagnostic.error.message : t("transverse.diagnostic.calculationError");
  const locationLabel = (location: string) => location === "HANDED_OVER_TO_CUSTOMER"
    ? t("transverse.diagnostic.handedOverToCustomer")
    : location === "UNLOCATED" ? t("transverse.diagnostic.unlocated") : location;
  const checkLabel = (code: string, message: string) => {
    const hu = message.split(" : ")[0] ?? "";
    const count = Number(message.match(/: (\d+)/)?.[1] ?? 0);
    const keyByCode: Record<string, string> = {
      NO_INCONSISTENCY: "noInconsistency",
      DOUBLE_ALLOCATION: "doubleAllocation",
      MULTIPLE_ACTIVE_PICKING_TASKS: "multipleActivePickingTasks",
      SOURCE_LOCATION_MISMATCH: "sourceLocationMismatch"
    };
    const key = keyByCode[code];
    return key ? t(`transverse.diagnostic.${key}`, { hu, count }) : message;
  };
  const categoryLabel = (category: string) => {
    const keyByCategory: Record<string, string> = {
      HU: "huCategory", FLUX: "flowCategory", ALLOCATION: "allocationCategory",
      MOUVEMENT: "movementCategory", PRÉPARATION: "pickingCategory",
      GÉNÉALOGIE: "genealogyCategory", "MISE EN STOCK": "putawayCategory",
      LIVRAISON: "deliveryCategory", PREPARATION_TASK: "pickingTaskCategory",
      PREPARATION_ORDER: "pickingCategory", PREPARATION_WAVE: "pickingWaveCategory",
      REPACKAGING: "repackagingCategory", MISSION: "deliveryCategory",
      CONSOLIDATION: "consolidationCategory"
    };
    const key = keyByCategory[category];
    return key ? t(`transverse.diagnostic.${key}`) : category;
  };
  const descriptionLabel = (description: string) => {
    const keyByDescription: Record<string, string> = {
      "Création de l’unité de manutention": "huCreated",
      "Trollet et destination confirmés par scan": "trolleyAndDestinationConfirmed",
      "Mission prise en charge": "missionTaken",
      "Mission créée vers la destination sélectionnée": "missionCreatedForDestination",
      "Mission directe créée vers la destination sélectionnée": "directMissionCreatedForDestination",
      "Mission créée après reconditionnement": "missionCreatedAfterRepackaging",
      "Tous les prélèvements sont confirmés": "allPicksConfirmed",
      "Prélèvement confirmé": "pickConfirmed",
      "Tâche prise en charge": "taskTaken",
      "Ordre créé depuis l’allocation": "orderCreatedFromAllocation",
      "Réservation libérée": "allocationReleased",
      "Expédition directe des HU d’origine": "originalHusShippedDirectly",
      "HU d’origine et destination confirmées": "originalHusAndDestinationConfirmed",
      "Reconditionnement créé en attente des transferts": "repackagingWaitingForTransfers",
      "Reconditionnement logistique de sortie créé": "outboundRepackagingCreated",
      "Reconditionnement pris en charge": "repackagingTaken"
    };
    const exactKey = keyByDescription[description];
    if (exactKey) return t(`transverse.diagnostic.${exactKey}`);
    const stage = description.match(/^Étape (.+) → (.+)$/);
    if (stage) return t("transverse.diagnostic.stageChanged", {
      previous: stage[1] === "initiale" ? t("transverse.diagnostic.initialStage") : stage[1],
      next: stage[2]
    });
    const reservation = description.match(/^Réservation (.+) (\S+)$/);
    if (reservation) return t("transverse.diagnostic.reservation", {
      quantity: reservation[1], uom: reservation[2]
    });
    const destination = description.match(/^Destination (.+)$/);
    if (destination) return t("transverse.diagnostic.destination", { destination: destination[1] });
    const movement = description.match(/^(.+) · quantité (.+) → (.+) (\S+)(?: · (.+))?$/);
    if (movement) return t("transverse.diagnostic.quantityMovement", {
      hu: movement[1], previous: movement[2], next: movement[3], uom: movement[4],
      comment: movement[5] ? ` · ${movement[5]}` : ""
    });
    const shippingHu = description.match(/^HU d’expédition (.+) créée$/);
    if (shippingHu) return t("transverse.diagnostic.shippingHuCreated", { hu: shippingHu[1] });
    const preparationHu = description.match(/^HU de préparation réservée : (.+)$/);
    if (preparationHu) return t("transverse.diagnostic.preparationHuReserved", { hu: preparationHu[1] });
    const returnableHu = description.match(/^HU retournable réservée : (.+)$/);
    if (returnableHu) return t("transverse.diagnostic.returnableHuReserved", { hu: returnableHu[1] });
    return description;
  };

  return <Box sx={{ width: "100%" }}>
    <PageHeader title={t("administration.entries.flowDiagnostics")} onBack={onBack}
      description={t("transverse.diagnostic.flowDescription")} />
    <Paper component="form" onSubmit={submit} variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField fullWidth value={input} onChange={(event) => setInput(event.target.value)}
          placeholder="Ex. HU-LIFO-003, PICK-…, PREP-…, WAVE-…, TOUR-…"
          slotProps={{ input: { startAdornment: <InputAdornment position="start">
            <SearchOutlined />
          </InputAdornment> } }} />
        <Button type="submit" variant="contained" disabled={input.trim().length < 3}>
          {t("transverse.diagnostic.run")}
        </Button>
      </Stack>
    </Paper>

    {diagnostic.isError && <Alert severity="error" sx={{ mb: 2 }}>
      {errorMessage}
    </Alert>}
    {diagnostic.data && <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800}>{t("transverse.diagnostic.foundObjects")}</Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {diagnostic.data.handlingUnits.map((hu) => <Chip key={hu.id}
            label={`${hu.huNumber} · ${hu.itemCode} · ${hu.currentQuantity} ${hu.uom}`
              + ` · ${locationLabel(hu.location)} · ${hu.status}`
              + (hu.splitSourceHandlingUnitNumber
                ? ` · ${t("transverse.diagnostic.splitFrom", { hu: hu.splitSourceHandlingUnitNumber })}` : "")}
            color={hu.splitSourceHandlingUnitNumber ? "secondary" : "default"}
            variant="outlined" />)}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800}>{t("transverse.diagnostic.consistency")}</Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {diagnostic.data.checks.map((check, index) => <Alert
            key={`${check.code}-${index}`}
            severity={check.severity === "ERROR" ? "error"
              : check.severity === "WARNING" ? "warning" : "success"}>
            {checkLabel(check.code, check.message)}
          </Alert>)}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800}>{t("transverse.diagnostic.timeline")}</Typography>
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {diagnostic.data.events.map((event, index) => <Box
            key={`${event.category}-${event.reference}-${event.occurredAt}-${index}`} sx={{ py: 1.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
              justifyContent="space-between">
              <Box>
                <Typography fontWeight={800}>{categoryLabel(event.category)} · {event.reference}</Typography>
                <Typography color="text.secondary">{descriptionLabel(event.description)}</Typography>
                {event.actor && <Typography variant="caption" color="text.secondary">
                  {t("transverse.diagnostic.by", { user: event.actor })}
                </Typography>}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={operationalStatusLabel(event.status)} />
                <Typography variant="caption" color="text.secondary">
                  {new Date(event.occurredAt).toLocaleString(i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR")}
                </Typography>
              </Stack>
            </Stack>
          </Box>)}
        </Stack>
      </Paper>
    </Stack>}
  </Box>;
}
